import { type Money, toMajor } from "./money";

/**
 * Currency formatting — presentation only.
 *
 * Formatting is locale-aware; arithmetic never is. This module is the boundary:
 * it takes an audited Money and produces a string for a human. Nothing here
 * returns a value that may re-enter a computation.
 */

export interface CurrencyFormat {
  currencyCode: string;
  currencySymbol: string;
  minorUnits: number;
  locale: string;
}

/** "₱285.50" */
export function formatMoney(amount: Money, fmt: CurrencyFormat): string {
  const value = toMajor(amount, fmt.minorUnits);
  const digits = new Intl.NumberFormat(fmt.locale, {
    minimumFractionDigits: fmt.minorUnits,
    maximumFractionDigits: fmt.minorUnits,
  }).format(Math.abs(value));
  const sign = amount < 0 ? "-" : "";
  return `${sign}${fmt.currencySymbol}${digits}`;
}

/** "285.50" — for receipt columns where the symbol sits in a header. */
export function formatAmount(amount: Money, fmt: CurrencyFormat): string {
  return new Intl.NumberFormat(fmt.locale, {
    minimumFractionDigits: fmt.minorUnits,
    maximumFractionDigits: fmt.minorUnits,
  }).format(toMajor(amount, fmt.minorUnits));
}

/** "12%" from 1200 basis points. */
export function formatRate(basisPoints: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(basisPoints / 10_000);
}
